/**
 * AYU Onboarding Fragebogen
 * Multi-Step Wizard: 63 Fragen in 3 Sektionen
 * Eine Frage pro Seite, Fortschrittsbalken, bedingte Folgefragen
 * Am Ende: Phänotyp-Berechnung + Coaching-Stil-Vorschlag
 */
import { useState, useCallback, useMemo, useEffect } from "react";
import { useLocation } from "wouter";
import { useClientAuth } from "@/contexts/ClientAuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  ChevronLeft,
  ChevronRight,
  Check,
  Loader2,
  Sparkles,
  Heart,
  Brain,
  Sun,
  Target,
  Activity,
} from "lucide-react";
import {
  SURVEY_SECTIONS,
  type SurveyQuestion,
  type SurveyAnswer,
  type SurveyResponse,
  type SubQuestion,
} from "@shared/surveySchema";
import {
  calculatePhenotype,
  PHENOTYPES,
  getPhenotypeLabel,
  getCoachingStyle,
  type PhenotypeResult,
} from "@shared/phenotypeScoring";
import { usePwaInstall } from "@/hooks/usePwaInstall";
import { IMAGES } from "@/lib/constants";

import { createLogger } from "@/lib/logger";
const log = createLogger("Apponboarding");

type SectionId = "goals" | "body" | "wellbeing" | "lifestyle";

const SECTION_ORDER: SectionId[] = ["goals", "body", "wellbeing", "lifestyle"];

const SECTION_ICONS: Record<SectionId, React.ReactNode> = {
  goals: <Target className="w-5 h-5" />,
  body: <Heart className="w-5 h-5" />,
  wellbeing: <Brain className="w-5 h-5" />,
  lifestyle: <Activity className="w-5 h-5" />,
};

export default function AppOnboarding() {
  const { client, loading, isAuthenticated, refetch } = useClientAuth();
  const [, navigate] = useLocation();
  const { t } = useI18n();

  // Auth guard - redirect to login if not authenticated
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate("/login");
    }
  }, [loading, isAuthenticated, navigate]);

  // Already completed onboarding - redirect to dashboard
  useEffect(() => {
    if (!loading && client?.onboardingComplete) {
      navigate("/app");
    }
  }, [loading, client, navigate]);

  const [currentSection, setCurrentSection] = useState<SectionId>("goals");
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, SurveyAnswer>>({});
  const [showResult, setShowResult] = useState(false);
  const [showTelegramStep, setShowTelegramStep] = useState(false);
  const [showPwaStep, setShowPwaStep] = useState(false);
  const [phenotypeResult, setPhenotypeResult] = useState<PhenotypeResult | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const saveAnamnese = trpc.clientAuth.saveAnamnese.useMutation();
  const saveDoshaProfile = trpc.clientAuth.saveDoshaProfile.useMutation();
  const saveCoachingStyle = trpc.clientAuth.saveCoachingStyle.useMutation();
  const determinePhenotype = trpc.clientAuth.determinePhenotype.useMutation();

  // Derive current question data (safe even when loading)
  const currentSectionData = SURVEY_SECTIONS[currentSection];
  const questions = currentSectionData.questions;
  const currentQuestion = questions[currentQuestionIndex];

  // Get visible sub-questions based on current answer
  const visibleSubQuestions = useMemo(() => {
    if (!currentQuestion?.subQuestions) return [];
    const answer = answers[currentQuestion.id];
    if (!answer) return [];

    return currentQuestion.subQuestions.filter((sq) => {
      if (!sq.condition) return true;
      const parentVal = answer.value;
      if (Array.isArray(sq.condition.parentValue)) {
        return sq.condition.parentValue.includes(String(parentVal));
      }
      return String(parentVal) === String(sq.condition.parentValue);
    });
  }, [currentQuestion, answers]);

  // Total progress
  const totalQuestions = useMemo(() => SECTION_ORDER.reduce(
    (sum, s) => sum + SURVEY_SECTIONS[s].questions.length,
    0
  ), []);
  const completedQuestions = useMemo(() => SECTION_ORDER.reduce((sum, s) => {
    if (SECTION_ORDER.indexOf(s) < SECTION_ORDER.indexOf(currentSection)) {
      return sum + SURVEY_SECTIONS[s].questions.length;
    }
    if (s === currentSection) {
      return sum + currentQuestionIndex;
    }
    return sum;
  }, 0), [currentSection, currentQuestionIndex]);
  const progressPercent = Math.round((completedQuestions / totalQuestions) * 100);

  // Section progress
  const sectionProgress = Math.round(
    ((currentQuestionIndex + 1) / questions.length) * 100
  );

  // ─── Answer Handlers ───

  const setAnswer = useCallback(
    (questionId: string, value: string | number | string[]) => {
      setAnswers((prev) => ({
        ...prev,
        [questionId]: {
          ...prev[questionId],
          questionId,
          value,
        },
      }));
    },
    []
  );

  const setOtherText = useCallback(
    (questionId: string, otherText: string) => {
      setAnswers((prev) => ({
        ...prev,
        [questionId]: {
          ...prev[questionId],
          questionId,
          value: prev[questionId]?.value ?? "",
          otherText,
        },
      }));
    },
    []
  );

  const setSubAnswer = useCallback(
    (questionId: string, subId: string, value: string | number | string[]) => {
      setAnswers((prev) => ({
        ...prev,
        [questionId]: {
          ...prev[questionId],
          questionId,
          value: prev[questionId]?.value ?? "",
          subAnswers: {
            ...(prev[questionId]?.subAnswers || {}),
            [subId]: value,
          },
        },
      }));
    },
    []
  );

  const currentAnswer = answers[currentQuestion?.id];

  // Auto-set default value for range/slider questions
  useEffect(() => {
    if (currentQuestion?.type === 'range' && !currentAnswer) {
      setAnswer(currentQuestion.id, currentQuestion.min || 1);
    }
  }, [currentQuestion, currentAnswer, setAnswer]);

  const canProceed = useMemo(() => {
    if (!currentQuestion) return false;
    // Alle Fragen sind standardmäßig required, außer explizit required: false
    const isRequired = currentQuestion.required !== false;
    if (!isRequired) return true;
    if (!currentAnswer) return false;
    const val = currentAnswer.value;
    if (val === "" || val === undefined || val === null) return false;
    if (Array.isArray(val) && val.length === 0) return false;
    // range_input: Beide Felder (Von + Bis) müssen ausgefüllt sein
    if (currentQuestion.type === "range_input" && typeof val === "string") {
      const parts = val.split("-");
      if (!parts[0] || !parts[1]) return false;
    }
    return true;
  }, [currentQuestion, currentAnswer]);

  // ─── Navigation ───

  const goNext = useCallback(async () => {
    setIsTransitioning(true);
    setTimeout(() => {
      if (currentQuestionIndex < questions.length - 1) {
        setCurrentQuestionIndex((i) => i + 1);
      } else {
        // Move to next section
        const sectionIdx = SECTION_ORDER.indexOf(currentSection);
        if (sectionIdx < SECTION_ORDER.length - 1) {
          setCurrentSection(SECTION_ORDER[sectionIdx + 1]);
          setCurrentQuestionIndex(0);
        } else {
          // All done → calculate phenotype
          finishOnboarding();
        }
      }
      setIsTransitioning(false);
    }, 200);
  }, [currentQuestionIndex, questions.length, currentSection, answers]);

  const goBack = useCallback(() => {
    setIsTransitioning(true);
    setTimeout(() => {
      if (currentQuestionIndex > 0) {
        setCurrentQuestionIndex((i) => i - 1);
      } else {
        const sectionIdx = SECTION_ORDER.indexOf(currentSection);
        if (sectionIdx > 0) {
          const prevSection = SECTION_ORDER[sectionIdx - 1];
          setCurrentSection(prevSection);
          setCurrentQuestionIndex(
            SURVEY_SECTIONS[prevSection].questions.length - 1
          );
        }
      }
      setIsTransitioning(false);
    }, 200);
  }, [currentQuestionIndex, currentSection]);

  const isFirstQuestion =
    currentSection === "goals" && currentQuestionIndex === 0;

  // ─── Finish ───

  const finishOnboarding = useCallback(async () => {
    if (!client) return;

    // Build SurveyResponse
    const response: SurveyResponse = {
      goals: [],
      body: [],
      wellbeing: [],
      lifestyle: [],
      completedAt: Date.now(),
      completedSections: ["goals", "body", "wellbeing", "lifestyle"],
    };

    for (const [qId, answer] of Object.entries(answers)) {
      const question = SURVEY_SECTIONS.goals.questions.find((q) => q.id === qId)
        || SURVEY_SECTIONS.body.questions.find((q) => q.id === qId)
        || SURVEY_SECTIONS.wellbeing.questions.find((q) => q.id === qId)
        || SURVEY_SECTIONS.lifestyle.questions.find((q) => q.id === qId);
      if (!question) continue;
      response[question.section as keyof SurveyResponse as "goals" | "body" | "wellbeing" | "lifestyle"].push(answer);
    }

    // Save anamnese first
    try {
      await saveAnamnese.mutateAsync({
        clientId: client.clientId,
        anamneseData: response,
      });
    } catch (err) {
      log.error("Save anamnese error:");
    }

    // LLM-basierte Phänotyp-Bestimmung (wie ChatGPT-Prompt)
    // Fallback: Regelbasiertes Scoring
    let result: PhenotypeResult;
    try {
      result = await determinePhenotype.mutateAsync({
        clientId: client.clientId,
        surveyResponse: response,
        clientName: client.firstName || undefined,
      }) as PhenotypeResult;
    } catch (err) {
      log.warn("LLM phenotype failed, using rule-based fallback:");
      result = calculatePhenotype(response);
    }
    setPhenotypeResult(result);

    // Save phenotype + coaching style
    try {
      await saveDoshaProfile.mutateAsync({
        clientId: client.clientId,
        doshaProfile: result,
      });
      const style = getCoachingStyle(result);
      await saveCoachingStyle.mutateAsync({
        clientId: client.clientId,
        coachingStyle: style,
      });
    } catch (err) {
      log.error("Save profile error:");
    }

    setShowResult(true);
  }, [client, answers, saveAnamnese, saveDoshaProfile, saveCoachingStyle, determinePhenotype]);

  const completeOnboarding = useCallback(() => {
    refetch();
    // The AppLayout will redirect to /app once onboardingComplete is true
  }, [refetch]);

  // ─── Early Returns (after all hooks) ───

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "linear-gradient(135deg, #f5f0e8 0%, #ede6d8 50%, #f5f0e8 100%)" }}>
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 animate-spin text-[#6b7f4e]" />
          <span className="text-[#8a9680] text-sm">{t("onboarding.laden")}</span>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) return null;

  // ─── Result Screen ───

  if (showResult && phenotypeResult) {
    const primary = PHENOTYPES[phenotypeResult.primary];
    const secondary = phenotypeResult.secondary
      ? PHENOTYPES[phenotypeResult.secondary]
      : null;

    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: "#f8f8f8" }}>
        <div className="w-full max-w-lg">
          <Card className="border-[#d4dcc4] bg-white overflow-hidden">
            {/* Header with gradient */}
            <div
              className="p-8 text-center"
              style={{
                background: `linear-gradient(135deg, ${primary.color}20, ${primary.color}05)`,
              }}
            >
              <Sparkles className="w-10 h-10 mx-auto mb-4" style={{ color: primary.color }} />
              <h1
                className="text-2xl font-bold text-[#2d3a1e] mb-2"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                {t("onboarding.deinPhenotyp")}
              </h1>
              <p className="text-[#5a6b4a] text-sm">
                {t("onboarding.basiertAufAntworten")}
              </p>
            </div>

            <CardContent className="p-6 space-y-6">
              {/* Primary Phenotype */}
              <div className="text-center">
                <div
                  className="inline-block px-4 py-2 rounded-full text-sm font-medium mb-3"
                  style={{
                    background: `${primary.color}20`,
                    color: primary.color,
                    border: `1px solid ${primary.color}40`,
                  }}
                >
                  {primary.cluster}
                </div>
                <h2
                  className="text-xl font-bold text-[#2d3a1e]"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {getPhenotypeLabel(phenotypeResult)}
                </h2>
                <p className="text-[#5a6b4a] text-sm mt-2">{primary.system}</p>
              </div>

              {/* Description */}
              <div className="bg-[#f8f8f8] rounded-lg p-4 border border-[#d4dcc4]">
                <p className="text-[#4a5e38] text-sm leading-relaxed">
                  {primary.description}
                </p>
                {phenotypeResult.isTwoInOne && secondary && (
                  <p className="text-[#5a6b4a] text-sm mt-3 pt-3 border-t border-[#d4dcc4]">
                    <strong className="text-[#6b7f4e]">{t("onboarding.zweiInEins")}</strong>{" "}
                    {t("onboarding.secondaryMerkmale", { name: secondary.name, system: secondary.system })}
                  </p>
                )}
              </div>

              {/* Confidence */}
              <div className="flex items-center justify-between text-sm">
                <span className="text-[#8a9680]">{t("onboarding.konfidenz")}</span>
                <span
                  className={`px-2 py-0.5 rounded text-xs font-medium ${
                    phenotypeResult.confidence === "high"
                      ? "bg-green-100 text-green-700"
                      : phenotypeResult.confidence === "medium"
                      ? "bg-yellow-100 text-yellow-700"
                      : "bg-red-100 text-red-700"
                  }`}
                >
                  {phenotypeResult.confidence === "high"
                    ? t("onboarding.hoch")
                    : phenotypeResult.confidence === "medium"
                    ? t("onboarding.mittel")
                    : t("onboarding.niedrig")}
                </span>
              </div>

              {/* Top 3 Scores */}
              <div className="space-y-2">
                <p className="text-xs text-[#8a9680] uppercase tracking-wider">{t("onboarding.topPhenotypen")}</p>
                {phenotypeResult.scores.filter(s => s.totalScore > 0).slice(0, 3).map((score) => {
                  const pheno = PHENOTYPES[score.phenotypeId];
                  const maxScore = phenotypeResult.scores[0].totalScore;
                  const percent = maxScore > 0 ? (score.totalScore / maxScore) * 100 : 0;
                  return (
                    <div key={score.phenotypeId} className="flex items-center gap-3">
                      <span className="text-xs text-[#5a6b4a] w-40 truncate">{pheno.name}</span>
                      <div className="flex-1 h-2 bg-[#f8f8f8] rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all duration-500"
                          style={{
                            width: `${percent}%`,
                            background: pheno.color,
                          }}
                        />
                      </div>
                      <span className="text-xs text-[#8a9680] w-8 text-right">
                        {score.totalScore.toFixed(1)}
                      </span>
                    </div>
                  );
                })}
              </div>

              {/* CTA */}
              <Button
                onClick={() => setShowPwaStep(true)}
                className="w-full bg-[#6b7f4e] hover:bg-[#5a6e3f] text-white font-medium py-6"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                {t("onboarding.weiter")}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // ─── PWA Install Step (after Phenotype Result) ───

  if (showPwaStep && !showTelegramStep) {
    return <PwaInstallStep onContinue={() => setShowTelegramStep(true)} />;
  }

  // ─── Telegram Connection Step (after PWA Install) ───

  if (showTelegramStep && client) {
    const coachBotUrl = `https://t.me/Ayu_health_bot?start=${client.clientId}`;
    const communityBotUrl = `https://t.me/Ayu_community_bot?start=ref_${client.clientId}`;

    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: "#f8f8f8" }}>
        <div className="w-full max-w-lg">
          <Card className="border-[#d4dcc4] bg-white overflow-hidden">
            {/* Header */}
            <div className="p-8 text-center bg-gradient-to-br from-[#0088cc]/5 to-[#6b7f4e]/5">
              <svg className="w-10 h-10 mx-auto mb-4 text-[#0088cc]" viewBox="0 0 24 24" fill="currentColor">
                <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
              </svg>
              <h1
                className="text-2xl font-bold text-[#2d3a1e] mb-2"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                {t("onboarding.telegramVerbinden")}
              </h1>
              <p className="text-[#5a6b4a] text-sm">
                {t("onboarding.telegramOptional")} <span className="text-[#8a9680]">{t("onboarding.optional")}</span>
              </p>
            </div>

            <CardContent className="p-6 space-y-5">
              {/* AYU Coach Bot */}
              <div className="bg-[#f8f8f8] rounded-xl p-4 border border-[#d4dcc4]">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 rounded-xl bg-[#0088cc]/10 flex items-center justify-center">
                    <svg className="w-4 h-4 text-[#0088cc]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-[#2d3a1e]">Annam</h3>
                    <p className="text-xs text-[#8a9680]">Dein persönlicher Gesundheitsbegleiter</p>
                  </div>
                </div>
                <p className="text-xs text-[#5a6b4a] mb-3 leading-relaxed">
                  Chatte mit Annam direkt über Telegram — für Tagebuch-Einträge, Gesundheitsfragen und tägliche Begleitung.
                </p>
                <a
                  href={coachBotUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-[#0088cc] text-white text-sm font-medium hover:bg-[#0077b5] transition-colors shadow-sm"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                  </svg>
                  {t("onboarding.annamOeffnen")}
                </a>
                <p className="text-[10px] text-[#8a9680] mt-2 text-center">
                  {t("onboarding.telegramHinweis")}
                </p>
              </div>

              {/* AYU Community Bot */}
              <div className="bg-[#f8f8f8] rounded-xl p-4 border border-[#d4dcc4]">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 rounded-xl bg-[#6b7f4e]/10 flex items-center justify-center">
                    <svg className="w-4 h-4 text-[#6b7f4e]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                      <circle cx="9" cy="7" r="4"/>
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                      <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-[#2d3a1e]">AYU Community</h3>
                    <p className="text-xs text-[#8a9680]">Austausch mit anderen Mitgliedern</p>
                  </div>
                </div>
                <p className="text-xs text-[#5a6b4a] mb-3 leading-relaxed">
                  Tritt der AYU-Community auf Telegram bei — tausche dich mit anderen Mitgliedern aus und erhalte Inspiration.
                </p>
                <a
                  href={communityBotUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-[#6b7f4e] text-white text-sm font-medium hover:bg-[#5a6b3e] transition-colors shadow-sm"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                  </svg>
                  {t("onboarding.communityBeitreten")}
                </a>
                <p className="text-[10px] text-[#8a9680] mt-2 text-center">
                  {t("onboarding.communityHinweis")}
                </p>
              </div>

              {/* CTA: Weiter zum Dashboard */}
              <Button
                onClick={completeOnboarding}
                className="w-full bg-[#6b7f4e] hover:bg-[#5a6e3f] text-white font-medium py-6"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                {t("onboarding.dashboardOeffnen")}
              </Button>

              <button
                onClick={completeOnboarding}
                className="w-full text-center text-xs text-[#8a9680] hover:text-[#6b7f4e] transition-colors py-1"
              >
                {t("onboarding.ueberspringen")}
              </button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // ─── Questionnaire UI ───

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "#f8f8f8" }}>
      {/* Top Bar */}
      <div className="sticky top-0 z-10 bg-white border-b border-[#d4dcc4] px-4 py-3">
        <div className="max-w-lg mx-auto">
          {/* Section Tabs */}
          <div className="flex items-center gap-2 mb-3">
            {SECTION_ORDER.map((s, idx) => {
              const isCompleted = SECTION_ORDER.indexOf(currentSection) > idx;
              const isCurrent = s === currentSection;
              return (
                <div key={s} className="flex items-center gap-2 flex-1">
                  <div
                    className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-xs transition-all ${
                      isCurrent
                        ? "bg-[#6b7f4e]/15 text-[#6b7f4e] font-medium"
                        : isCompleted
                        ? "text-green-500"
                        : "text-[#8a9680]"
                    }`}
                  >
                    {isCompleted ? (
                      <Check className="w-3.5 h-3.5" />
                    ) : (
                      SECTION_ICONS[s]
                    )}
                    <span className="hidden sm:inline">
                      {SURVEY_SECTIONS[s].title}
                    </span>
                    <span className="sm:hidden">{idx + 1}</span>
                  </div>
                  {idx < SECTION_ORDER.length - 1 && (
                    <div
                      className={`flex-1 h-px ${
                        isCompleted ? "bg-green-500/50" : "bg-[#6b7f4e]/10"
                      }`}
                    />
                  )}
                </div>
              );
            })}
          </div>

          {/* Progress Bar */}
          <div className="flex items-center gap-3">
            <div className="flex-1 h-1.5 bg-[#f8f8f8] rounded-full overflow-hidden">
              <div
                className="h-full bg-[#6b7f4e] rounded-full transition-all duration-300"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
            <span className="text-xs text-[#8a9680] whitespace-nowrap">
              {completedQuestions}/{totalQuestions}
            </span>
          </div>
        </div>
      </div>

      {/* Question Content */}
      <div className="flex-1 flex items-center justify-center p-4">
        <div
          className={`w-full max-w-lg transition-opacity duration-200 ${
            isTransitioning ? "opacity-0" : "opacity-100"
          }`}
        >
          {/* Category Badge */}
          <div className="mb-4">
            <span className="text-xs text-[#6b7f4e]/60 uppercase tracking-wider">
              {currentQuestion?.category}
            </span>
          </div>

          {/* Question */}
          <h2
            className="text-xl sm:text-2xl font-bold text-[#2d3a1e] mb-8 leading-snug"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {currentQuestion?.text}
          </h2>

          {/* Answer Input */}
          <div className="space-y-3">
            <QuestionRenderer
              question={currentQuestion}
              answer={currentAnswer}
              onAnswer={(val) => setAnswer(currentQuestion.id, val)}
            />

            {/* Other Text Input */}
            {currentQuestion?.showOtherInput && (
              (currentQuestion.type === 'multiselect'
                ? Array.isArray(currentAnswer?.value) && (currentAnswer.value as string[]).includes('other')
                : currentAnswer?.value === 'other'
              ) && (
                <div className="mt-4">
                  <Input
                    value={currentAnswer?.otherText || ''}
                    onChange={(e) => setOtherText(currentQuestion.id, e.target.value)}
                    placeholder={t("onboarding.bitterGenauer")}
                    className="bg-white border-[#d4dcc4] text-[#2d3a1e] focus:border-[#6b7f4e] placeholder:text-[#8a9680]"
                  />
                </div>
              )
            )}

            {/* Sub-Questions */}
            {visibleSubQuestions.map((sq) => (
              <div key={sq.id} className="mt-6 pl-4 border-l-2 border-[#d4dcc4]">
                <p className="text-sm text-[#5a6b4a] mb-3">{sq.text}</p>
                <SubQuestionRenderer
                  subQuestion={sq}
                  value={currentAnswer?.subAnswers?.[sq.id]}
                  onAnswer={(val) => setSubAnswer(currentQuestion.id, sq.id, val)}
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom Navigation */}
      <div className="sticky bottom-0 bg-white border-t border-[#d4dcc4] px-4 py-4">
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={goBack}
            disabled={isFirstQuestion}
            className="text-[#5a6b4a] hover:text-[#2d3a1e] hover:bg-[#6b7f4e]/10"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            {t("onboarding.zurueck")}
          </Button>

          <span className="text-xs text-[#8a9680]">
            {currentQuestionIndex + 1} / {questions.length}
          </span>

          <Button
            onClick={goNext}
            disabled={!canProceed}
            className="bg-[#6b7f4e] hover:bg-[#5a6e3f] text-white font-medium"
          >
            {currentQuestionIndex === questions.length - 1 &&
            SECTION_ORDER.indexOf(currentSection) === SECTION_ORDER.length - 1
              ? t("onboarding.auswerten")
              : t("onboarding.weiter")}
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Question Renderers ───

function QuestionRenderer({
  question,
  answer,
  onAnswer,
}: {
  question: SurveyQuestion;
  answer?: SurveyAnswer;
  onAnswer: (val: string | number | string[]) => void;
}) {
  const { t } = useI18n();
  const value = answer?.value;

  switch (question.type) {
    case "radio":
    case "select":
    case "frequency":
      return (
        <div className="grid gap-2">
          {question.options?.map((opt) => (
            <button
              key={opt.value}
              onClick={() => onAnswer(opt.value)}
              className={`text-left px-4 py-3 rounded-lg border transition-all text-sm ${
                value === opt.value
                  ? "border-[#6b7f4e] bg-[#6b7f4e]/10 text-[#2d3a1e]"
                  : "border-[#d4dcc4] bg-white text-[#5a6b4a] hover:border-[#6b7f4e]/30 hover:bg-[#6b7f4e]/5"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      );

    case "multiselect":
      const selected = Array.isArray(value) ? value : [];
      return (
        <div className="grid gap-2">
          {question.options?.map((opt) => {
            const isSelected = selected.includes(opt.value);
            return (
              <button
                key={opt.value}
                onClick={() => {
                  if (isSelected) {
                    onAnswer(selected.filter((v) => v !== opt.value));
                  } else {
                    onAnswer([...selected, opt.value]);
                  }
                }}
                className={`text-left px-4 py-3 rounded-lg border transition-all text-sm flex items-center gap-3 ${
                  isSelected
                    ? "border-[#6b7f4e] bg-[#6b7f4e]/10 text-[#2d3a1e]"
                    : "border-[#d4dcc4] bg-white text-[#5a6b4a] hover:border-[#6b7f4e]/30 hover:bg-[#6b7f4e]/5"
                }`}
              >
                <div
                  className={`w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 ${
                    isSelected
                      ? "border-[#6b7f4e] bg-[#6b7f4e]"
                      : "border-[#6b7f4e]/30"
                  }`}
                >
                  {isSelected && <Check className="w-3 h-3 text-white" />}
                </div>
                {opt.label}
              </button>
            );
          })}
          <p className="text-xs text-[#8a9680] mt-1">{t("onboarding.mehrfachauswahl")}</p>
        </div>
      );

    case "yesno":
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            { value: "yes", label: t("onboarding.ja") },
            { value: "no", label: t("onboarding.nein") },
          ].map((opt) => (
            <button
              key={opt.value}
              onClick={() => onAnswer(opt.value)}
              className={`px-6 py-4 rounded-lg border transition-all text-base font-medium ${
                value === opt.value
                  ? "border-[#6b7f4e] bg-[#6b7f4e]/10 text-[#2d3a1e]"
                  : "border-[#d4dcc4] bg-white text-[#5a6b4a] hover:border-[#6b7f4e]/30"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      );

    case "range":
      const rangeVal = typeof value === "number" ? value : question.min || 1;
      return (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-xs text-[#8a9680]">{question.min || 1}</span>
            <span className="text-3xl font-bold text-[#6b7f4e]">{rangeVal}</span>
            <span className="text-xs text-[#8a9680]">{question.max || 10}</span>
          </div>
          <Slider
            value={[rangeVal]}
            min={question.min || 1}
            max={question.max || 10}
            step={1}
            onValueChange={([v]) => onAnswer(v)}
            className="[&_[role=slider]]:bg-[#6b7f4e] [&_[role=slider]]:border-[#6b7f4e] [&_.range]:bg-[#6b7f4e]"
          />
          <div className="flex justify-between text-xs text-[#8a9680]">
            <span>{t("onboarding.niedrigLabel")}</span>
            <span>{t("onboarding.hochLabel")}</span>
          </div>
        </div>
      );

    case "range_input": {
      // Von-Bis Eingabe (z.B. Schlaf: 6–8 Stunden)
      const rangeValue = typeof value === "string" ? value : "";
      const parts = rangeValue.split("-");
      const fromVal = parts[0] || "";
      const toVal = parts[1] || "";
      return (
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <label className="text-xs text-[#8a9680] mb-1 block">{question.fromLabel || t("onboarding.von")}</label>
            <Input
              type="number"
              value={fromVal}
              onChange={(e) => {
                const newFrom = e.target.value;
                onAnswer(`${newFrom}-${toVal}`);
              }}
              min={question.min}
              max={question.max}
              placeholder="z.B. 6"
              className="bg-white border-[#d4dcc4] text-[#2d3a1e] text-lg py-6 focus:border-[#6b7f4e] placeholder:text-[#8a9680]"
            />
          </div>
          <span className="text-[#8a9680] text-lg mt-5">–</span>
          <div className="flex-1">
            <label className="text-xs text-[#8a9680] mb-1 block">{question.toLabel || t("onboarding.bis")}</label>
            <Input
              type="number"
              value={toVal}
              onChange={(e) => {
                const newTo = e.target.value;
                onAnswer(`${fromVal}-${newTo}`);
              }}
              min={question.min}
              max={question.max}
              placeholder="z.B. 8"
              className="bg-white border-[#d4dcc4] text-[#2d3a1e] text-lg py-6 focus:border-[#6b7f4e] placeholder:text-[#8a9680]"
            />
          </div>
          {question.unit && (
            <span className="text-[#8a9680] text-sm mt-5">{question.unit}</span>
          )}
        </div>
      );
    }

    case "number":
      return (
        <Input
          type="number"
          value={value !== undefined ? String(value) : ""}
          onChange={(e) => onAnswer(Number(e.target.value))}
          min={question.min}
          max={question.max}
          placeholder={question.placeholder || ""}
          className="bg-white border-[#d4dcc4] text-[#2d3a1e] text-lg py-6 focus:border-[#6b7f4e] placeholder:text-[#8a9680]"
        />
      );

    case "text":
      return (
        <Textarea
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onAnswer(e.target.value)}
          placeholder={question.placeholder || t("onboarding.deinAntwort")}
          rows={3}
          className="bg-white border-[#d4dcc4] text-[#2d3a1e] focus:border-[#6b7f4e] placeholder:text-[#8a9680] resize-none"
        />
      );

    case "preference":
      return (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {[
            { value: "yes", label: t("onboarding.ja") },
            { value: "neutral", label: t("onboarding.neutral") },
            { value: "no", label: t("onboarding.nein") },
          ].map((opt) => (
            <button
              key={opt.value}
              onClick={() => onAnswer(opt.value)}
              className={`px-4 py-3 rounded-lg border transition-all text-sm ${
                value === opt.value
                  ? "border-[#6b7f4e] bg-[#6b7f4e]/10 text-[#2d3a1e]"
                  : "border-[#d4dcc4] bg-white text-[#5a6b4a] hover:border-[#6b7f4e]/30"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      );

    default:
      return (
        <Input
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onAnswer(e.target.value)}
          placeholder={t("onboarding.deinAntwort")}
          className="bg-white border-[#d4dcc4] text-[#2d3a1e] focus:border-[#6b7f4e]"
        />
      );
  }
}

function SubQuestionRenderer({
  subQuestion,
  value,
  onAnswer,
}: {
  subQuestion: SubQuestion;
  value?: string | number | string[];
  onAnswer: (val: string | number | string[]) => void;
}) {
  const { t } = useI18n();
  switch (subQuestion.type) {
    case "text":
      return (
        <Textarea
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onAnswer(e.target.value)}
          placeholder={subQuestion.placeholder || t("onboarding.deinAntwort")}
          rows={2}
          className="bg-white border-[#d4dcc4] text-[#2d3a1e] focus:border-[#6b7f4e] placeholder:text-[#8a9680] resize-none text-sm"
        />
      );

    case "select":
    case "radio":
      return (
        <div className="grid gap-2">
          {subQuestion.options?.map((opt) => (
            <button
              key={opt.value}
              onClick={() => onAnswer(opt.value)}
              className={`text-left px-3 py-2 rounded-lg border transition-all text-sm ${
                value === opt.value
                  ? "border-[#6b7f4e] bg-[#6b7f4e]/10 text-[#2d3a1e]"
                  : "border-[#d4dcc4] bg-white text-[#5a6b4a] hover:border-[#6b7f4e]/30"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      );

    default:
      return (
        <Input
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onAnswer(e.target.value)}
          placeholder={subQuestion.placeholder || ""}
          className="bg-white border-[#d4dcc4] text-[#2d3a1e] focus:border-[#6b7f4e] text-sm"
        />
      );
  }
}


// ─── PWA Install Step Component ───

function PwaInstallStep({ onContinue }: { onContinue: () => void }) {
  const { canInstall, isInstalled, isIOS, installPwa, hasNativePrompt } = usePwaInstall();
  const { t } = useI18n();
  const [installTriggered, setInstallTriggered] = useState(false);

  const handleInstall = async () => {
    if (hasNativePrompt) {
      const success = await installPwa();
      if (success) {
        setInstallTriggered(true);
        // Auto-continue after successful install
        setTimeout(onContinue, 1500);
      }
    } else if (isIOS) {
      // iOS: just mark as triggered since we can only show instructions
      setInstallTriggered(true);
    }
  };

  // If already installed, skip this step
  if (isInstalled) {
    onContinue();
    return null;
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: "#f8f8f8" }}>
      <div className="w-full max-w-lg">
        <Card className="border-[#d4dcc4] bg-white overflow-hidden">
          {/* Header */}
          <div className="p-8 text-center bg-gradient-to-br from-[#6b7f4e]/5 to-[#c8a97e]/5">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-[#6b7f4e] to-[#5a6b3e] flex items-center justify-center shadow-lg">
              <img
                src={IMAGES.logo}
                alt="AYU"
                className="w-10 h-10 object-contain"
              />
            </div>
            <h1
              className="text-2xl font-bold text-[#2d3a1e] mb-2"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {t("onboarding.pwaTitle")}
            </h1>
            <p className="text-[#5a6b4a] text-sm">
              {t("onboarding.pwaSubtitle")}
            </p>
          </div>

          <CardContent className="p-6 space-y-5">
            {/* Benefits */}
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-[#6b7f4e]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Sparkles className="w-4 h-4 text-[#6b7f4e]" />
                </div>
                <div>
                  <p className="text-sm font-medium text-[#2d3a1e]">{t("onboarding.sofortzugriff")}</p>
                  <p className="text-xs text-[#8a9680]">{t("onboarding.sofortzugriffDesc")}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-[#6b7f4e]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Activity className="w-4 h-4 text-[#6b7f4e]" />
                </div>
                <div>
                  <p className="text-sm font-medium text-[#2d3a1e]">{t("onboarding.vollbild")}</p>
                  <p className="text-xs text-[#8a9680]">{t("onboarding.vollbildDesc")}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-[#6b7f4e]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check className="w-4 h-4 text-[#6b7f4e]" />
                </div>
                <div>
                  <p className="text-sm font-medium text-[#2d3a1e]">{t("onboarding.keinDownload")}</p>
                  <p className="text-xs text-[#8a9680]">{t("onboarding.keinDownloadDesc")}</p>
                </div>
              </div>
            </div>

            {/* Install Action */}
            {installTriggered ? (
              <div className="bg-[#6b7f4e]/10 rounded-xl p-4 text-center border border-[#d4dcc4]">
                <Check className="w-8 h-8 text-[#6b7f4e] mx-auto mb-2" />
                <p className="text-sm font-medium text-[#2d3a1e]">
                  {isIOS ? t("onboarding.iosGemacht") : t("onboarding.appInstalliert")}
                </p>
              </div>
            ) : isIOS ? (
              /* iOS Instructions with visual icons */
              <div className="bg-[#f8f8f8] rounded-xl p-4 border border-[#d4dcc4]">
                <p className="text-sm font-medium text-[#2d3a1e] mb-3">{t("onboarding.iosAnleitung")}</p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-[#6b7f4e] text-white text-xs flex items-center justify-center font-bold flex-shrink-0 mt-0.5">1</span>
                    <p className="text-sm text-[#5a6b4a] leading-relaxed">
                      {t("onboarding.iosSchritt1")}
                      <span className="inline-flex items-center justify-center w-6 h-6 bg-[#007AFF] rounded ml-1.5 align-middle">
                        <svg className="w-3.5 h-3.5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                          <polyline points="16 6 12 2 8 6" />
                          <line x1="12" y1="2" x2="12" y2="15" />
                        </svg>
                      </span>
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-[#6b7f4e] text-white text-xs flex items-center justify-center font-bold flex-shrink-0 mt-0.5">2</span>
                    <p className="text-sm text-[#5a6b4a] leading-relaxed">
                      {t("onboarding.iosSchritt2")}
                      <span className="inline-flex items-center justify-center w-6 h-6 bg-gray-100 rounded ml-1.5 align-middle">
                        <svg className="w-3.5 h-3.5 text-gray-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="3" y="3" width="18" height="18" rx="2" />
                          <line x1="12" y1="8" x2="12" y2="16" />
                          <line x1="8" y1="12" x2="16" y2="12" />
                        </svg>
                      </span>
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-[#6b7f4e] text-white text-xs flex items-center justify-center font-bold flex-shrink-0 mt-0.5">3</span>
                    <p className="text-sm text-[#5a6b4a] leading-relaxed">
                      {t("onboarding.iosSchritt3")}
                    </p>
                  </div>
                </div>
                <Button
                  onClick={handleInstall}
                  className="w-full mt-4 bg-[#6b7f4e] hover:bg-[#5a6e3f] text-white font-medium"
                >
                  <Check className="w-4 h-4 mr-2" />
                  {t("onboarding.verstanden")}
                </Button>
              </div>
            ) : hasNativePrompt ? (
              /* Android/Chrome native install */
              <Button
                onClick={handleInstall}
                className="w-full bg-[#6b7f4e] hover:bg-[#5a6e3f] text-white font-medium py-6 text-base"
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                {t("onboarding.appInstallieren")}
              </Button>
            ) : (
              /* Fallback: manual instructions */
              <div className="bg-[#f8f8f8] rounded-xl p-4 border border-[#d4dcc4]">
                <p className="text-sm font-medium text-[#2d3a1e] mb-3">{t("onboarding.soGehtEs")}</p>
                <div className="space-y-2.5">
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-[#6b7f4e] text-white text-xs flex items-center justify-center font-bold flex-shrink-0">1</span>
                    <p className="text-xs text-[#5a6b4a]">
                      {t("onboarding.browserMenue")}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-[#6b7f4e] text-white text-xs flex items-center justify-center font-bold flex-shrink-0">2</span>
                    <p className="text-xs text-[#5a6b4a]">
                      {t("onboarding.startbildschirm")}
                    </p>
                  </div>
                </div>
                <Button
                  onClick={() => setInstallTriggered(true)}
                  className="w-full mt-4 bg-[#6b7f4e] hover:bg-[#5a6e3f] text-white font-medium"
                >
                  <Check className="w-4 h-4 mr-2" />
                  {t("onboarding.verstanden")}
                </Button>
              </div>
            )}

            {/* Continue */}
            <Button
              onClick={onContinue}
              className="w-full bg-[#6b7f4e] hover:bg-[#5a6e3f] text-white font-medium py-6"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              {t("onboarding.weiter")}
            </Button>

            <button
              onClick={onContinue}
              className="w-full text-center text-xs text-[#8a9680] hover:text-[#6b7f4e] transition-colors py-1"
            >
              {t("onboarding.ueberspringenSpaeter")}
            </button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
